import React from "react";
var __jsx = React.createElement;
import { UnstyledButton } from "@mantine/core";
import { useRef } from "react";
import { Dialog } from "@headlessui/react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faCopy, faPrint, faUserDoctor, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { CopyToClipboard } from "@/components/copy-to-clipboard";
import { formatDate } from "@/utils/format";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { DocumentContentView } from "./document-content-view";
import { extractPlainText, isTextContentType } from "./document-content";
/** Modal viewer for a resolved encounter document, with print and copy actions. */
export function MedicalReportModal(_ref) {
  var show = _ref.show,
    onClose = _ref.onClose,
    document = _ref.document;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var printRef = useRef(null);
  var print = useReactToPrint({
    contentRef: printRef,
    documentTitle: document.name
  });
  var content = document.htmlContent !== undefined ? document.htmlContent : document.url;
  var showActions = isTextContentType(document.contentType);
  var provider = document.encounter.provider || t("not_available");
  return /*#__PURE__*/React.createElement(Dialog, {
    open: show,
    onClose: onClose,
    className: "relative z-sds-1200"
  }, /*#__PURE__*/React.createElement("div", {
    className: "fixed inset-0 bg-sds-overlay",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("div", {
    className: "fixed inset-0 flex items-center justify-center p-4"
  }, /*#__PURE__*/React.createElement(Dialog.Panel, {
    className: "flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-sds-400 bg-sds-base-default shadow-sds-600"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-start justify-between gap-4 bg-sds-brand-default px-6 py-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "min-w-0"
  }, /*#__PURE__*/React.createElement(Dialog.Title, {
    className: "truncate text-style-body-strong text-sds-brand-on-brand"
  }, document.name), document.encounter.location ? /*#__PURE__*/React.createElement("p", {
    className: "truncate text-style-body-small text-sds-brand-on-brand opacity-90"
  }, document.encounter.location) : null), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-shrink-0 items-center gap-4 text-sds-brand-on-brand"
  }, /*#__PURE__*/React.createElement("span", {
    className: "flex items-center gap-2 text-style-body-small"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faUserDoctor,
    className: "h-3.5 w-3.5"
  }), provider), /*#__PURE__*/React.createElement("span", {
    className: "flex items-center gap-2 text-style-body-small"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faClock,
    className: "h-3.5 w-3.5"
  }), formatDate(document.encounter.start_date)), /*#__PURE__*/React.createElement(UnstyledButton, {
    type: "button",
    "data-testid": "medical-report-close",
    "aria-label": t("close"),
    title: t("close"),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.MEDICAL_REPORT_CLOSE);
      onClose();
    },
    className: "rounded-sds-full p-2 hover:bg-sds-brand-secondary"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faXmark,
    className: "h-4 w-4"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "flex-1 overflow-y-auto scrollbar-sds p-6"
  }, /*#__PURE__*/React.createElement(DocumentContentView, {
    content: content,
    contentType: document.contentType,
    name: document.name
  })), showActions ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2 border-t border-sds-base-default bg-sds-base-secondary px-6 py-4"
  }, /*#__PURE__*/React.createElement(UnstyledButton, {
    type: "button",
    "data-testid": "medical-report-print",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.MEDICAL_REPORT_PRINT);
      print();
    },
    className: "flex items-center gap-2 px-3 py-2 text-style-body-small text-sds-base-secondary hover:text-sds-brand-default"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPrint,
    className: "h-4 w-4"
  }), t("print")), /*#__PURE__*/React.createElement(CopyToClipboard, {
    value: extractPlainText(content, document.contentType)
  }, function (_ref2) {
    var copied = _ref2.copied,
      copy = _ref2.copy;
    return /*#__PURE__*/React.createElement(UnstyledButton, {
      type: "button",
      "data-testid": "medical-report-copy",
      onClick: function onClick() {
        trackEvent(ANALYTICS_EVENTS.MEDICAL_REPORT_COPY);
        copy();
      },
      className: "flex items-center gap-2 px-3 py-2 text-style-body-small text-sds-base-secondary hover:text-sds-brand-default"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faCopy,
      className: "h-4 w-4"
    }), copied ? t("copied") : t("copy"));
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: "h-0 overflow-hidden"
  }, /*#__PURE__*/React.createElement("div", {
    ref: printRef,
    className: "m-8 flex flex-col gap-4 text-sds-base-default"
  }, /*#__PURE__*/React.createElement("h1", {
    className: "text-style-heading-main"
  }, document.name), /*#__PURE__*/React.createElement(DocumentContentView, {
    content: content,
    contentType: document.contentType,
    name: document.name,
    forPrint: true
  }))))));
}